import type { PurchaseOrder } from '@erp/rpc';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  FileTextIcon,
  BuildingsIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  PackageIcon,
  HashIcon,
  LinkIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { SystemEntityInline } from '@/components/system-entities';
import { SystemEntity, EntityAssociationType } from '@erp/shared-types';
import { useEntityAssociationsList } from '@/api/rpc/hooks/core/useEntityAssociations';
import { useMemo } from 'react';
import { useAPInvoice } from '@/api/rpc/hooks/accounts-payable/useAPInvoices';

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved',
  SENT: 'Sent',
  PARTIALLY_RECEIVED: 'Partially Received',
  FULLY_RECEIVED: 'Fully Received',
  PARTIALLY_INVOICED: 'Partially Invoiced',
  FULLY_INVOICED: 'Fully Invoiced',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

const STATUS_VARIANTS: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  DRAFT: 'outline',
  PENDING_APPROVAL: 'secondary',
  APPROVED: 'default',
  SENT: 'default',
  PARTIALLY_RECEIVED: 'secondary',
  FULLY_RECEIVED: 'default',
  PARTIALLY_INVOICED: 'secondary',
  FULLY_INVOICED: 'default',
  CLOSED: 'outline',
  CANCELLED: 'destructive',
};

// Helper function to format currency
const formatCurrencyValue = (
  value: string | undefined | null,
  currency = 'USD',
): string => {
  if (!value) return 'N/A';

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;

  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
};

interface PurchaseOrderMetadataProps {
  purchaseOrder: PurchaseOrder;
  hasPaymentHoldLines?: boolean;
}

export function PurchaseOrderMetadata({
  purchaseOrder,
  hasPaymentHoldLines,
}: PurchaseOrderMetadataProps) {
  // Fetch associated invoices
  const invoiceAssociationsQuery = useEntityAssociationsList({
    entityId: purchaseOrder.id,
    entityType: SystemEntity.INTERNAL_PURCHASE_ORDER,
    associationType: EntityAssociationType.AP_INVOICE_MATCHING,
  });

  const invoiceAssociations = useMemo(
    () => invoiceAssociationsQuery.data?.associations || [],
    [invoiceAssociationsQuery.data?.associations],
  );

  // Filter to only get invoice-level associations (not line items)
  const linkedInvoices = useMemo(
    () =>
      invoiceAssociations.filter(
        (assoc) => assoc.entity.entityType === SystemEntity.AP_INVOICE,
      ),
    [invoiceAssociations],
  );

  // Get the first linked invoice to show its date
  const firstLinkedInvoiceId = linkedInvoices[0]?.entity.entityId;
  const { invoice: firstLinkedInvoice } = useAPInvoice(
    firstLinkedInvoiceId,
    !!firstLinkedInvoiceId,
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileTextIcon className="h-4 w-4" />
            {purchaseOrder.externalId || 'No PO Number'}
          </CardTitle>
          <Badge variant={STATUS_VARIANTS[purchaseOrder.status] || 'outline'}>
            {STATUS_LABELS[purchaseOrder.status] || purchaseOrder.status}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {hasPaymentHoldLines && (
          <div className="flex items-center gap-2 text-amber-600 bg-amber-50 border border-amber-200 rounded-md p-2">
            <WarningIcon className="h-4 w-4" />
            <span className="text-sm">Some line items are on payment hold</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <HashIcon className="h-3 w-3" />
              Internal ID
            </div>
            <div className="font-medium text-xs font-mono">
              {purchaseOrder.internalId}
            </div>
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <BuildingsIcon className="h-3 w-3" />
              Supplier
            </div>
            <div className="flex items-start">
              <SystemEntityInline
                entityType={SystemEntity.AP_SUPPLIER}
                entityId={purchaseOrder.supplierId}
              />
            </div>
          </div>

          {/* Linked Invoices */}
          {linkedInvoices.length > 0 && (
            <div className="space-y-0.5 col-span-2">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <LinkIcon className="h-3 w-3" />
                Linked Invoice{linkedInvoices.length > 1 ? 's' : ''}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {linkedInvoices.map((invoiceAssoc) => (
                  <SystemEntityInline
                    key={invoiceAssoc.entity.entityId}
                    entityType={invoiceAssoc.entity.entityType}
                    entityId={invoiceAssoc.entity.entityId}
                    className="text-xs"
                  />
                ))}
              </div>
            </div>
          )}

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CurrencyDollarIcon className="h-3 w-3" />
              Total Amount
            </div>
            <div className="text-lg font-bold text-green-600">
              {formatCurrencyValue(
                purchaseOrder.totalAmount,
                purchaseOrder.currency || undefined,
              )}
            </div>
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <PackageIcon className="h-3 w-3" />
              Received
            </div>
            <div className="font-medium text-sm">
              {purchaseOrder.receivedFlag ? 'Yes' : 'No'}
            </div>
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              Order Date
            </div>
            <div className="font-medium text-sm">
              {formatDateOnlyString(purchaseOrder.orderDate)}
            </div>
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              {firstLinkedInvoice?.invoice?.invoiceDate
                ? 'Invoice Date'
                : 'Delivery Date'}
            </div>
            <div className="font-medium text-sm">
              {firstLinkedInvoice?.invoice?.invoiceDate
                ? formatDateOnlyString(firstLinkedInvoice.invoice.invoiceDate)
                : purchaseOrder.deliveryDate
                  ? formatDateOnlyString(purchaseOrder.deliveryDate)
                  : 'Not specified'}
            </div>
          </div>
        </div>

        {/* Additional Details */}
        {(purchaseOrder.referenceId ||
          purchaseOrder.salesOrderId ||
          purchaseOrder.originSystem) && (
          <div className="border-t pt-3 space-y-2">
            {purchaseOrder.referenceId && (
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  Reference ID
                </div>
                <div className="text-xs font-mono">
                  {purchaseOrder.referenceId}
                </div>
              </div>
            )}
            {purchaseOrder.salesOrderId && (
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  Sales Order ID
                </div>
                <div className="text-xs font-mono">
                  {purchaseOrder.salesOrderId}
                </div>
              </div>
            )}
            {purchaseOrder.originSystem && (
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  Origin System
                </div>
                <div className="text-xs">{purchaseOrder.originSystem}</div>
              </div>
            )}
          </div>
        )}

        {/* PDF Link */}
        {purchaseOrder.pdfS3Url && (
          <div className="border-t pt-3">
            <a
              href={purchaseOrder.pdfS3Url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:underline flex items-center gap-1"
            >
              <FileTextIcon className="h-3 w-3" />
              View PDF
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
